// A chart family that can also be drawn as a word cloud.
//
// `chartMode: 'wc'` is not a chart builder — on the live page the block drops its chart entirely and
// hands the question to the open-ended view. Headless needs the same fork, and it has to happen
// ABOVE the builder lookup: a chart mode the registry does not know either resolves to null (silence
// until the watchdog fires) or, on the batched path, falls back to the default builder and exports a
// perfectly successful PNG of the wrong chart.

import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { buildBlockWords } from '@aytm/app/stats/page/question-views/components/word-cloud/utils/blockWords'

import HeadlessWordCloud from '@aytm/app/stats/page/question-views/components/word-cloud/HeadlessWordCloud'
import { isWcChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

// The export canvas the render-shell fixes its container at.
const DEFAULT_WC_WIDTH = 746

/**
 * Wrap a chart component so `chartMode: 'wc'` renders the block's word cloud instead.
 *
 * @param {any} Chart - the component that draws every other chart mode
 * @param {string} displayName
 * @returns {any}
 */
export const withWordCloudMode = (Chart, displayName) => {
  const Dispatcher = props => {
    const {
      chartMode,
      questionId,
      questionKey,
      qType,
      answer,
      batches,
      answerVariants,
      rType,
      wcConfig,
      wcWidth,
      wcHeight
    } = props

    if (!isWcChartMode(chartMode)) return <Chart {...props} />

    // The block's own words go to the cloud unprocessed — they are answer texts, not a corpus.
    const rawData = { words: buildBlockWords({ type: qType, answer, batches, answerVariants, rType }) }

    return (
      <HeadlessWordCloud
        id={questionId}
        questionKey={questionKey}
        rawData={rawData}
        wcConfig={wcConfig}
        width={wcWidth || DEFAULT_WC_WIDTH}
        height={wcHeight}
        useRawData
      />
    )
  }

  Dispatcher.propTypes = {
    chartMode: PropTypes.string,
    questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    questionKey: PropTypes.string,
    qType: PropTypes.string,
    answer: answerPropType,
    batches: PropTypes.array,
    answerVariants: PropTypes.array,
    rType: PropTypes.string,
    wcConfig: PropTypes.object,
    wcWidth: PropTypes.number,
    wcHeight: PropTypes.number
  }

  Dispatcher.displayName = displayName

  return Dispatcher
}

export default withWordCloudMode
